import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InsightsWidgetHeader, ProfileInsightsStates } from '../components/insights/InsightsWidgetHeader';
import { NavigationProps } from '../components/insights/Navigation';
import { InsightsWidgetFooter } from '../components/insights/InsightsWidgetFooter';
import { DashboardContext } from '../contexts/DashboardContext';
import { DoughnutChart } from '../components/insights/DoughnutChart';
import { SingleBarChart } from '../components/insights/SingleBarChart';
import { ProfitInsightsWidgetSkeleton } from '../components/skeletons/ProfitInsightsWidgetSkeleton';
import { getStartTimeForPeriodType } from '../utils/insights.utils';
import { useStore } from '../../../store';
import { fetchNewInsightsByType, InsightType } from '../queryFunctions/fetchInsightsByType';
import { VIEWS_MAP } from '../../../router/constants';
import { WidgetLink } from '../components/WidgetLink';
import { trackMixPanel } from '../../../services/mixpanel.service';
import bugsnagHandler from '../../../services/bugsnag.service';

export enum ExpensesInsightState {
  Chart = 'DoughnutChart',
  Bars = 'MonthlyChart',
}

interface ExpensesInsightProps {
  isLoading?: boolean;
}

export const testIdPrefix = 'ExpensesInsightsWidget';

const navOptionsInitialState = {
  isNext: false,
  isPrevious: false,
  onNext: () => false,
  onPrevious: () => false,
};

export const ExpensesInsight = ({ isLoading }: ExpensesInsightProps) => {
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const { expensesChartData, chartFilters } = useContext(DashboardContext);
  const { customerId } = useStore((state) => state.currUser());
  const [currentExpensesInsightState, setCurrentExpensesInsightState] = useState<ExpensesInsightState>(
    ExpensesInsightState.Chart,
  );
  const [periodType, setPeriodType] = useState<ProfileInsightsStates>(ProfileInsightsStates.Yearly);
  const [subPeriodType, setSubPeriodType] = useState<string>((new Date()).getFullYear().toString());
  const [shouldUseInitialData, setShouldUseInitialData] = useState<boolean>(true);
  const [isLoadingNewData, setIsLoadingNewData] = useState<boolean>(false);
  const [newFetchedData, setNewFetchedData] = useState<any>();
  const expensesInsightsMainData = shouldUseInitialData ? expensesChartData : newFetchedData?.data;
  const [navOptions, setNavOptions] = useState<NavigationProps>(navOptionsInitialState);

  useEffect(() => {
    trackMixPanel('Act on Expenses Widget', { Action: periodType });
    trackMixPanel('Act on Expenses Widget', { Action: subPeriodType });

    if (currentExpensesInsightState === ExpensesInsightState.Chart) {
      trackMixPanel('Act on Expenses Widget', { WidgetView: 'Summary View' });
    }

    if (currentExpensesInsightState === ExpensesInsightState.Bars) {
      trackMixPanel('Act on Expenses Widget', { WidgetView: 'Timeline View' });
    }
  }, [periodType, subPeriodType, currentExpensesInsightState]);

  useEffect(() => {
    if (currentExpensesInsightState === ExpensesInsightState.Chart && periodType !== ProfileInsightsStates.Monthly) {
      setNavOptions({
        isNext: true,
        isPrevious: false,
        onNext: () => {
          setCurrentExpensesInsightState(ExpensesInsightState.Bars);
          trackMixPanel('Act on Expenses Widget', { Action: 'Arrow Right' });
        },
        onPrevious: () => false,
      });
    } else {
      setNavOptions(navOptionsInitialState);
    }

    if (currentExpensesInsightState === ExpensesInsightState.Bars) {
      setNavOptions({
        isNext: false,
        isPrevious: true,
        onNext: () => false,
        onPrevious: () => {
          setCurrentExpensesInsightState(ExpensesInsightState.Chart);
          trackMixPanel('Act on Expenses Widget', { Action: 'Arrow Left' });
        },
      });
    }
  }, [currentExpensesInsightState, periodType, expensesChartData]);

  useEffect(() => {
    if (expensesChartData) {
      setCurrentExpensesInsightState(
        expensesChartData?.categoriesItems.length > 0 ? ExpensesInsightState.Chart : ExpensesInsightState.Bars,
      );
    }
    if (!expensesChartData?.categoriesItems?.length) {
      setNavOptions(navOptionsInitialState);
    }
  }, [expensesChartData]);

  const handleFilterChange = (state: ProfileInsightsStates, subOption: string) => {
    const startTime = getStartTimeForPeriodType(chartFilters, state, subOption);
    if (startTime) {
      setIsLoadingNewData(true);
      fetchNewInsightsByType(customerId, state, startTime, InsightType.Expenses)
        .then((data: any) => {
          if (shouldUseInitialData) setShouldUseInitialData(false);
          setPeriodType(state);
          setSubPeriodType(subOption);
          setNewFetchedData(data);
        })
        .catch((e) => {
          bugsnagHandler.notify(
            `FetchInsightsByType error message: ${e?.message} and props: customerId: ${customerId} , periodType: ${state}, startTime: ${startTime}, insightType: ${InsightType.Expenses}`,
          );
        })
        .finally(() => {
          setIsLoadingNewData(false);
        });
    }
  };

  const renderActionButton = () => {
    const handleAllExpenses = () => {
      trackMixPanel('Act on Expenses Widget', { Action: 'All Expenses' });
    };
    return (
      <div className='flex'>
        <WidgetLink
          onClick={handleAllExpenses}
          linkTitle={t('WIDGET_LATEST_TRANSACTIONS.EXPENSES_BUTTON')}
          linkUrl={`/${VIEWS_MAP.TRANSACTIONS}`}
          testId={`${testIdPrefix}_manage-link`}
        />
      </div>
    );
  };
  if (isLoading || isLoadingNewData) return <ProfitInsightsWidgetSkeleton />;

  return (
    <div
      className='flex flex-col bg-bw00 justify-between rounded-[12px] p-[19px] pb-[13px] h-full'
      data-testid={`${testIdPrefix}__wrapper`}
    >
      <InsightsWidgetHeader
        title={t('INSIGHT_WIDGET.EXPENSES_TITLE')}
        chartSummaryData={expensesInsightsMainData?.chartSummaryData}
        onStateChange={handleFilterChange}
        showChartIndex={false}
        chartFilters={chartFilters}
        periodType={periodType}
        subPeriodType={subPeriodType}
        isMonthlyPeriodDisabled={currentExpensesInsightState === ExpensesInsightState.Bars}
      />

      <div data-testid={`${testIdPrefix}__GraphsWrapper`}>
        {currentExpensesInsightState === ExpensesInsightState.Chart && (
          <DoughnutChart expensesChartData={expensesInsightsMainData} />
        )}

        {expensesInsightsMainData?.segmentedItems && currentExpensesInsightState === ExpensesInsightState.Bars && (
          <SingleBarChart
            tooltipTitle='Expenses'
            segmentedItems={expensesInsightsMainData?.segmentedItems}
            originInsightType={InsightType.Expenses}
          />
        )}

        <InsightsWidgetFooter {...navOptions} rightActionButton={renderActionButton()} />
      </div>
    </div>
  );
};
